import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Users,
  Search,
  Clock,
  UserCheck,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Filter,
  Tag,
  Settings,
  Save
} from 'lucide-react';
import { adminCompanyAPI, adminSettingsAPI } from '../../services/api';

const STORAGE_KEY = 'companies-filters';
const SETTINGS_KEY = 'companies.defaultFilters';

// Get saved filters from localStorage
const getSavedFilters = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

// Save filters to localStorage
const saveFilters = (filters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
};

export default function CompaniesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const savedFilters = getSavedFilters();
  const [adminDefaults, setAdminDefaults] = useState(null);
  const [adminDefaultsLoaded, setAdminDefaultsLoaded] = useState(false);
  const initialLoadDone = useRef(false);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [companyTypes, setCompanyTypes] = useState([]);
  const [savingDefault, setSavingDefault] = useState(false);
  const [showDefaultMenu, setShowDefaultMenu] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });

  // Get initial value with priority: URL > localStorage > admin defaults > hardcoded
  const getInitialValue = (param, hardcodedDefault = '') => {
    const urlValue = searchParams.get(param);
    if (urlValue !== null) return urlValue;

    const localValue = savedFilters[param];
    if (localValue !== undefined) return localValue;

    if (adminDefaults && adminDefaults[param] !== undefined) {
      return adminDefaults[param];
    }

    return hardcodedDefault;
  };

  // Update URL and localStorage when filters change
  const updateFilters = (updates) => {
    const newFilters = {
      type: updates.type ?? typeFilter,
      showInactive: updates.showInactive ?? showInactive,
      search: updates.search ?? search,
      page: updates.page ?? pagination.page,
    };

    // Update URL params
    const newParams = new URLSearchParams();
    if (newFilters.type) newParams.set('type', newFilters.type);
    if (newFilters.showInactive) newParams.set('showInactive', 'true');
    if (newFilters.search) newParams.set('search', newFilters.search);
    if (newFilters.page > 1) newParams.set('page', newFilters.page.toString());
    setSearchParams(newParams);

    // Save to localStorage
    saveFilters(newFilters);
  };

  // Fetch admin defaults and company types on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch admin defaults
        const settingsResponse = await adminSettingsAPI.get(SETTINGS_KEY);
        if (settingsResponse.data?.setting?.value) {
          setAdminDefaults(settingsResponse.data.setting.value);
        }
      } catch (err) {
        // Setting might not exist, use hardcoded defaults
        console.log('No admin defaults configured');
      }
      setAdminDefaultsLoaded(true);

      // Fetch company types
      try {
        const typesResponse = await adminCompanyAPI.getTypes();
        setCompanyTypes(typesResponse.data.types || []);
      } catch (err) {
        console.error('Failed to fetch company types:', err);
      }
    };
    fetchInitialData();
  }, []);

  // Apply initial filter values once admin defaults are loaded
  useEffect(() => {
    if (adminDefaultsLoaded && !initialLoadDone.current) {
      initialLoadDone.current = true;

      const initialType = getInitialValue('type', '');
      const initialShowInactive = getInitialValue('showInactive', 'false') === 'true';
      const initialSearch = getInitialValue('search', '');
      const initialPage = parseInt(getInitialValue('page', '1')) || 1;

      setTypeFilter(initialType);
      setShowInactive(initialShowInactive);
      setSearch(initialSearch);
      setPagination(prev => ({ ...prev, page: initialPage }));

      fetchCompanies(initialPage, initialSearch, initialType, initialShowInactive);
    }
  }, [adminDefaultsLoaded]);

  const fetchCompanies = async (page = 1, searchTerm = search, type = typeFilter, inactive = showInactive) => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminCompanyAPI.list({
        page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        type: type || undefined,
        showInactive: inactive ? 'true' : undefined
      });
      setCompanies(response.data.companies);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ search, page: 1 });
    fetchCompanies(1, search);
  };

  const handleTypeFilterChange = (newType) => {
    setTypeFilter(newType);
    updateFilters({ type: newType, page: 1 });
    fetchCompanies(1, search, newType, showInactive);
  };

  const handleShowInactiveChange = (newValue) => {
    setShowInactive(newValue);
    updateFilters({ showInactive: newValue, page: 1 });
    fetchCompanies(1, search, typeFilter, newValue);
  };

  const handlePageChange = (newPage) => {
    updateFilters({ page: newPage });
    fetchCompanies(newPage, search, typeFilter, showInactive);
  };

  const handleSync = async () => {
    if (syncing) return;

    setSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      const response = await adminCompanyAPI.syncAll();
      setSyncResult(response.data);
      // Refresh the list and types after sync
      await fetchCompanies(1, search);
      const typesResponse = await adminCompanyAPI.getTypes();
      setCompanyTypes(typesResponse.data.types || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync companies');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminCompanyAPI.export({ search });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `companies-export-${new Date().toLocaleDateString('en-CA')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export companies');
    }
  };

  const handleSaveAsDefault = async () => {
    setSavingDefault(true);
    try {
      const currentFilters = {
        type: typeFilter,
        showInactive: showInactive,
      };
      await adminSettingsAPI.set(SETTINGS_KEY, currentFilters, 'Default filters for the Companies admin page');
      setAdminDefaults(currentFilters);
      setShowDefaultMenu(false);
    } catch (err) {
      setError('Failed to save default filters');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleClearLocalStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDefaultMenu(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDefaultMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-settings-dropdown')) {
        setShowDefaultMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDefaultMenu]);

  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Companies</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {pagination.total} companies synced from ConnectWise
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from CW'}
          </button>
        </div>
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-green-700 dark:text-green-300">
            <CheckCircle className="w-5 h-5" />
            <p>
              Sync complete: {syncResult.synced} companies processed
              ({syncResult.added} new, {syncResult.updated} updated)
            </p>
          </div>
          <button onClick={() => setSyncResult(null)} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-400" />
            <select
              value={typeFilter}
              onChange={(e) => handleTypeFilterChange(e.target.value)}
              className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
            >
              <option value="">All Types</option>
              {companyTypes.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.type} ({t.count})
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => handleShowInactiveChange(e.target.checked)}
              className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500"
            />
            Show inactive
          </label>

          {/* Settings dropdown */}
          <div className="relative filter-settings-dropdown">
            <button
              onClick={() => setShowDefaultMenu(!showDefaultMenu)}
              className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              title="Filter settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            {showDefaultMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-10">
                <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
                  <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Filter Defaults</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleSaveAsDefault}
                    disabled={savingDefault}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <Save className="w-4 h-4" />
                    {savingDefault ? 'Saving...' : 'Save current as admin default'}
                  </button>
                  <button
                    onClick={handleClearLocalStorage}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <XCircle className="w-4 h-4" />
                    Clear my saved preference
                  </button>
                </div>
                {adminDefaults && (
                  <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 text-xs text-neutral-500 dark:text-neutral-400">
                    <p className="font-medium mb-1">Current admin defaults:</p>
                    <p>Type: {adminDefaults.type || 'All'}</p>
                    <p>Show inactive: {adminDefaults.showInactive ? 'Yes' : 'No'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : companies.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-neutral-400 dark:text-neutral-500" />
          <p className="text-neutral-500 dark:text-neutral-400 mb-4">No companies found</p>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync from ConnectWise
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company) => (
            <div
              key={company.id || company.cw_company_id}
              onClick={() => {
                sessionStorage.setItem('adminCompaniesReturnUrl', window.location.pathname + window.location.search);
                navigate(`/admin/companies/${company.cw_company_id}`);
              }}
              className={`bg-white dark:bg-neutral-800 rounded-lg border cursor-pointer ${
                company.is_active === false
                  ? 'border-neutral-300 dark:border-neutral-600 opacity-60'
                  : 'border-neutral-200 dark:border-neutral-700'
              } p-5 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm transition-all`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  company.user_count > 0
                    ? 'bg-blue-100 dark:bg-blue-900/50'
                    : 'bg-neutral-100 dark:bg-neutral-700'
                }`}>
                  <Building2 className={`w-6 h-6 ${
                    company.user_count > 0
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-neutral-400 dark:text-neutral-500'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-neutral-900 dark:text-white truncate">
                    {company.name || `Company #${company.cw_company_id}`}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                    <span>{company.identifier || `ID: ${company.cw_company_id}`}</span>
                    {company.status && (
                      <>
                        <span className="text-neutral-300 dark:text-neutral-600">|</span>
                        <span>{company.status}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags placeholder - can be expanded later */}
              {company.type && (
                <div className="mt-3 flex flex-wrap gap-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded">
                    <Tag className="w-3 h-3" />
                    {company.type}
                  </span>
                  {company.territory && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded">
                      {company.territory}
                    </span>
                  )}
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-neutral-400" />
                  <span className="text-neutral-600 dark:text-neutral-300">
                    {company.user_count || 0} user{company.user_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-neutral-400" />
                  <span className="text-neutral-600 dark:text-neutral-300">
                    {company.admin_count || 0} admin{company.admin_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-700">
                <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    <span>Active: {formatRelativeDate(company.last_activity)}</span>
                  </div>
                  {!company.portal_enabled && (
                    <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-300 rounded text-xs">
                      Portal Disabled
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} companies
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-neutral-600 dark:text-neutral-400">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
